import React, { useEffect, useState } from 'react';
import { Icon, loadIcon } from '@iconify/react';
import Honeybadger from '@honeybadger-io/js';

type Props = {
  name: string;
};

// Rendered when the requested icon can't be resolved
const FALLBACK_ICON = 'ic:baseline-help-outline';

const IconifyIcon: React.FC<Props> = ({ name }) => {
  // The icon name to render once it has been fetched from the Iconify API
  const [iconifyIconName, setIconifyIconName] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setIconifyIconName(null);

    loadIcon(name)
      .then(() => {
        if (active) {
          setIconifyIconName(name);
        }
      })
      .catch((err) => {
        if (!active) {
          return;
        }
        Honeybadger.notify(err, {
          message: `Icon not found via Iconify. Falling back to default icon, ${FALLBACK_ICON}.`,
          context: { iconName: name },
        });
        setIconifyIconName(FALLBACK_ICON);
      });

    return () => {
      active = false;
    };
  }, [name]);

  // Not fetched yet (also the SSR path, since the effect is client-only).
  if (!iconifyIconName) {
    return null;
  }

  // Iconify renders inline SVG sized at 1em with `currentColor`, so it inherits
  // the parent's fontSize and color just like an MUI SvgIcon.
  // However, Iconify icons seem to render ~33% larger, so we scale down to match MUI sizing.
  return <Icon icon={iconifyIconName} style={{ transform: 'scale(0.75)' }} />;
};

export default IconifyIcon;
